/**
 * TeamSwitcher.js
 * 
 * 🔒 SICHERHEITSKRITISCHE KOMPONENTE
 * 
 * Ermöglicht das Wechseln zwischen verschiedenen Team-Accounts.
 * Verwendet striktes Blocking während des Switches um Datenvermischung zu verhindern.
 */

import React, { useState, useRef, useEffect } from 'react';
import { useActiveWorld } from '../hooks/useActiveWorld';
import './TeamSwitcher.css';

const OVERLAY_MIN_DURATION = 700;
const OVERLAY_FADE_OUT = 300;

const TeamSwitcher = () => {
  const {
    availableTeams,
    activeTeam,
    hasMultipleTeams,
    switchTeam,
    isSwitching,
    isOwner,
    isLoading
  } = useActiveWorld();

  const [isOpen, setIsOpen] = useState(false);
  const [overlayVisible, setOverlayVisible] = useState(false);
  const [overlayExiting, setOverlayExiting] = useState(false);
  const dropdownRef = useRef(null);
  const overlayStartRef = useRef(0);

  useEffect(() => {
    if (isSwitching && !overlayVisible) {
      overlayStartRef.current = Date.now();
      setOverlayExiting(false);
      setOverlayVisible(true);
    }

    if (!isSwitching && overlayVisible && !overlayExiting) {
      const elapsed = Date.now() - overlayStartRef.current;
      const remaining = Math.max(0, OVERLAY_MIN_DURATION - elapsed);

      const delayTimer = setTimeout(() => {
        setOverlayExiting(true);
      }, remaining);

      const fadeTimer = setTimeout(() => {
        setOverlayVisible(false);
        setOverlayExiting(false);
      }, remaining + OVERLAY_FADE_OUT);

      return () => {
        clearTimeout(delayTimer);
        clearTimeout(fadeTimer);
      };
    }
  }, [isSwitching, overlayVisible, overlayExiting]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Zeige nichts wenn nur ein Account oder noch lädt
  if (isLoading || !hasMultipleTeams || availableTeams.length <= 1) {
    return null;
  }

  const handleSwitch = async (teamId) => {
    if (teamId === activeTeam?.teamId) {
      setIsOpen(false);
      return;
    }

    setIsOpen(false);
    
    // 🔒 Switch durchführen - UI wird automatisch geblockt
    const success = await switchTeam(teamId);
    
    if (!success) {
      console.error('Team switch failed');
    }
  };

  // SVG Icons statt Emojis
  const HomeIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
      <polyline points="9 22 9 12 15 12 15 22"/>
    </svg>
  );

  const StarIcon = () => (
    <svg viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
    </svg>
  );

  const UserIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
      <circle cx="12" cy="7" r="4"/>
    </svg>
  );

  const LockIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
      <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
    </svg>
  );

  const getTeamIcon = (team) => {
    if (team.isOwnAccount) return <HomeIcon />;
    const label = team.ownerName || team.ownerEmail || team.teamName || 'T';
    const initial = label.charAt(0).toUpperCase();
    return <span className="team-switcher-initial">{initial}</span>;
  };

  const getTeamLabel = (team) => {
    const owner = team.ownerName || team.ownerEmail;
    if (team.isOwnAccount) return owner || 'My Account';
    return owner || team.teamName || 'Team';
  };

  return (
    <>
      {overlayVisible && (
        <div className={`team-switcher-blocking-overlay ${overlayExiting ? 'ts-overlay-exit' : 'ts-overlay-enter'}`}>
          <div className="team-switcher-blocking-content">
            <img 
              src="/img/BonusClub_Logo.webp" 
              alt="BonusClub" 
              className="team-switcher-logo"
            />
            <div className="team-switcher-loading-bar">
              <div className="team-switcher-loading-progress"></div>
            </div>
            <p className="team-switcher-loading-text">Switching account...</p>
          </div>
        </div>
      )}

      <div className="team-switcher" ref={dropdownRef}>
        <button
          className={`team-switcher-trigger ${isOpen ? 'open' : ''}`}
          onClick={() => setIsOpen(!isOpen)}
          disabled={isSwitching}
        >
          <span className="team-switcher-name">{getTeamLabel(activeTeam)}</span>
          <svg 
            className={`team-switcher-arrow ${isOpen ? 'rotated' : ''}`}
            viewBox="0 0 24 24" 
            fill="none" 
            stroke="currentColor" 
            strokeWidth="2"
          >
            <polyline points="6,9 12,15 18,9"/>
          </svg>
        </button>

        {isOpen && (
          <div className="team-switcher-dropdown">
            <div className="team-switcher-header">
              <span>Switch Account</span>
              <span className="team-switcher-count">{availableTeams.length} accounts</span>
            </div>
            
            <div className="team-switcher-list">
              {availableTeams.map((team) => (
                <button
                  key={team.teamId}
                  className={`team-switcher-item ${team.teamId === activeTeam?.teamId ? 'active' : ''}`}
                  onClick={() => handleSwitch(team.teamId)}
                >
                  <span className="team-item-icon">{getTeamIcon(team)}</span>
                  <div className="team-item-info">
                    <span className="team-item-name">{getTeamLabel(team)}</span>
                    {!team.isOwnAccount && (
                      <span className="team-item-role">
                        {team.memberType === 'master' ? 'Full Access' : 'Leads Only'}
                      </span>
                    )}
                  </div>
                  {team.teamId === activeTeam?.teamId && (
                    <span className="team-item-check">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                        <polyline points="20,6 9,17 4,12"/>
                      </svg>
                    </span>
                  )}
                </button>
              ))}
            </div>

          </div>
        )}
      </div>
    </>
  );
};

export default TeamSwitcher;

